import { useState, useCallback, useEffect, useRef } from 'react';
import useWebSocket from './useWebSocket';
import { getTrialUsage } from '../../../utils/api/trialUsage_apis';
import {
  normalizeReplayStepStart,
  normalizeReplayStepComplete,
  normalizeReplayCompletePayload,
} from '../../../utils/automation/replayWsPayloadNormalize';
import { getScriptCreatorCap, TOKENS_PER_CREDIT } from '../../../constants/trialLimits';
import {
  parseTrialUsageResponse,
  canStartAutomationGenerator,
  canStartAutomationReplay,
  usageExceedsScriptCreatorCap,
  getEffectiveScriptCreatorCap,
  isLlmCompletionLogMessage,
} from '../../../utils/trialUsageGuards';
import {
  getWebSocketConnectErrorMessage,
  isWebSocketConnectSuccess,
} from '../../../utils/commonfunctions';
import { shouldApplyGenerationEvent, extractGenerationEventRunId, USER_ABORTED_EXECUTION_MESSAGE } from '../../../utils/automation/generationRunGuards';

/**
 * Adds sequential stepIndex to mainAction and related array objects
 * @param {Object} data - The execution flow data
 * @returns {Object} - Processed data with sequential stepIndex values
 */
const addSequentialStepIndices = (data) => {
  if (!data || !data.executions || !Array.isArray(data.executions)) {
    return data;
  }

  let globalStepIndex = 0;

  const processedExecutions = data.executions.map((execution) => {
    const processedExecution = { ...execution };

    // Add stepIndex to mainAction
    if (processedExecution.mainAction) {
      processedExecution.mainAction = {
        ...processedExecution.mainAction,
        stepIndex: globalStepIndex++
      };
    }

    // Add stepIndex to each object in related array
    if (processedExecution.related && Array.isArray(processedExecution.related)) {
      processedExecution.related = processedExecution.related.map((relatedItem) => ({
        ...relatedItem,
        stepIndex: globalStepIndex++
      }));
    }

    return processedExecution;
  });

  return {
    ...data,
    executions: processedExecutions
  };
};

/**
 * Specialized hook for automation WebSocket events
 * Provides clean interface for automation-specific functionality
 */
const useAutomationWebSocket = (config = {}) => {
  // WebSocket connection
  const setUsageDataRefs = useCallback((usageData) => {
    if (!usageData) return;
    creditsUsedAtStartRef.current = Number(usageData.credits_used) ?? 0;
    const capFromApi = Number(usageData.script_creator_cap);
    scriptCreatorCapRef.current = Number.isFinite(capFromApi) && capFromApi >= 0
      ? capFromApi
      : getScriptCreatorCap(usageData.credit_limit);
    tokensPerCreditRef.current = Number(usageData.tokens_per_credit) ?? TOKENS_PER_CREDIT;
  }, []);

  const {
    isConnected,
    isConnecting,
    error: connectionError,
    connect,
    disconnect,
    emit,
    on,
    clearError
  } = useWebSocket({
    url: config.url || process.env.REACT_APP_WS_URL || 'ws://localhost:3003',
    path: '/test-automation/socket.io/',
    transports: ['websocket', 'polling'],
    withCredentials: true,
    heartbeatInterval: 60000,
    ...config,
    onConnectWithUsage: setUsageDataRefs,
  });

  // Automation state
  const [logs, setLogs] = useState([]);
  const [testSteps, setTestSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [screenshots, setScreenshots] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [hierarchicalData, setHierarchicalData] = useState(null);
  const [executionFlow, setExecutionFlow] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [testCaseCompleteResult, setTestCaseCompleteResult] = useState(null);
  const [error, setError] = useState(null);
  const [replayExecuting, setReplayExecuting] = useState(false);
  const [replayCurrentStepIndex, setReplayCurrentStepIndex] = useState(null);
  const [replayExecutionResults, setReplayExecutionResults] = useState(null);
  const [replayError, setReplayError] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionResults, setExecutionResults] = useState(null);
  const [replayPlan, setReplayPlan] = useState(null);
  const [isReplayPlanFetched, setIsReplayPlanFetched] = useState(false);
  
  // Refs for scroll management
  const logsEndRef = useRef(null);
  const creditsUsedAtStartRef = useRef(0);
  const scriptCreatorCapRef = useRef(0);
  const tokensPerCreditRef = useRef(TOKENS_PER_CREDIT);
  const cumulativeTokensThisRunRef = useRef(0);
  const onCreditLimitReachedRef = useRef(config.onCreditLimitReached);
  const onScriptCreatorRunCompleteRef = useRef(config.onScriptCreatorRunComplete);
  const onReplayCompleteRef = useRef(config.onReplayComplete);
  const usageCheckInProgressRef = useRef(false);
  onCreditLimitReachedRef.current = config.onCreditLimitReached;
  onScriptCreatorRunCompleteRef.current = config.onScriptCreatorRunComplete;
  onReplayCompleteRef.current = config.onReplayComplete;

  // Refs for replay stop-on-fail: store executionId, executionContext, options when replayAutomation is called
  const replayExecutionIdRef = useRef(null);
  const replayExecutionContextRef = useRef(null);
  const replayOptionsRef = useRef(null);
  // Active generate run id — cleared on stop so late agent events are ignored (QPN-2087).
  const activeGenerationIdRef = useRef(null);

  // Cleanup functions registry
  const cleanupFunctionsRef = useRef([]);

  // Scroll to bottom utility
  const scrollToBottom = useCallback(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  /**
   * Always tear down any existing socket and open a new one before generate/replay.
   * Prevents reusing a connection that may still deliver events from a stopped run.
   */
  const ensureFreshConnection = useCallback(async () => {
    disconnect();
    return connect();
  }, [disconnect, connect]);

  // Clear all automation data
  const clearAutomationData = useCallback(() => {
    cumulativeTokensThisRunRef.current = 0;
    activeGenerationIdRef.current = null;
    setLogs([]);
    setTestSteps([]);
    setCurrentStep(0);
    setScreenshots([]);
    setTokenUsage(null);
    setHierarchicalData(null);
    setExecutionFlow(null);
    setReplayExecuting(false);
    setReplayCurrentStepIndex(null);
    setReplayExecutionResults(null);
    setReplayError(null);
    setExecutionLogs([]);
    setExecutionResults(null);
    setIsRunning(false);
    setTestCompleted(false);
    setTestCaseCompleteResult(null);
    setError(null);
    setReplayPlan(null);
    setIsReplayPlanFetched(false);
  }, []);

  /** Call at the start of a new replay (after clearAutomationData) so replayExecuting is true before async connect; avoids progress map being wiped by idle effects. */
  const markReplayStarting = useCallback(() => {
    setReplayExecutionResults(null);
    setReplayCurrentStepIndex(null);
    setReplayError(null);
    setReplayExecuting(true);
  }, []);

  const finalizeCreditExhausted = useCallback((usage) => {
    const cap = getEffectiveScriptCreatorCap(usage);
    const msg = `Credits exhausted, Please upgrade your plan.`;
    setError(msg);
    const replaying = replayExecutionIdRef.current != null;
    if (replaying) {
      setReplayExecuting(false);
      setReplayCurrentStepIndex(null);
      const execId = replayExecutionIdRef.current;
      const execCtx = replayExecutionContextRef.current;
      if (execId != null && execCtx != null) {
        emit('stopReplay', { uniqueId: execId, executionContext: execCtx }, () => {});
      }
    } else {
      setIsRunning(false);
      setTestCompleted(true);
      setTestCaseCompleteResult({ error: msg });
    }
    onCreditLimitReachedRef.current?.({ preserveData: true, message: msg });
    onScriptCreatorRunCompleteRef.current?.();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trialUsageRefresh'));
    }
    disconnect();
  }, [emit, disconnect]);

  // Set up automation event listeners
  useEffect(() => {
    console.log('[useAutomationWebSocket] 🎧 Event listener setup effect triggered');
    console.log('[useAutomationWebSocket] 🎧 isConnected:', isConnected);

    if (!isConnected) {
      console.log('[useAutomationWebSocket] 🎧 Not connected, skipping listener setup');
      return;
    }

    console.log('[useAutomationWebSocket] ✅ Setting up event listeners...');
    const cleanupFunctions = [];

    /** Accept streaming events for the active generate OR active replay; reject when idle/aborted. */
    const isActiveRunStreamEvent = (payload) => {
      const genId = activeGenerationIdRef.current;
      if (genId != null && genId !== '') {
        return shouldApplyGenerationEvent(genId, payload);
      }
      const replayId = replayExecutionIdRef.current;
      if (replayId != null && replayId !== '') {
        const eventId = extractGenerationEventRunId(payload);
        if (eventId == null || eventId === '') return true;
        return String(eventId) === String(replayId);
      }
      return false;
    };

    // Log events
    const logCleanup = on('log', (data) => {
      if (!isActiveRunStreamEvent(data)) return;
      console.log('[useAutomationWebSocket] 📝 Received log event:', data);
      if (data && typeof data === 'object') {
        setLogs(prev => {
          const newLogs = [...prev, {
            ...data,
            timestamp: data.timestamp || Date.now(),
            id: data.id || `log_${Date.now()}_${Math.random()}`
          }];
          
          // Limit logs to prevent memory issues (keep last 1000)
          return newLogs.slice(-1000);
        });
        
        // Auto-scroll to bottom
        setTimeout(scrollToBottom, 100);

        const msg = typeof data.message === 'string' ? data.message : '';
        if (isLlmCompletionLogMessage(msg) && onCreditLimitReachedRef.current && !usageCheckInProgressRef.current) {
          usageCheckInProgressRef.current = true;
          getTrialUsage().then((res) => {
            usageCheckInProgressRef.current = false;
            if (res?.status !== 'success' || !res?.data) return;
            if (!usageExceedsScriptCreatorCap(res.data)) return;
            finalizeCreditExhausted(res.data);
          }).catch(() => { usageCheckInProgressRef.current = false; });
        }
      }
    });
    cleanupFunctions.push(logCleanup);

    // Test step events
    const testStepCleanup = on('testStep', (step) => {
      if (!isActiveRunStreamEvent(step)) return;
      console.log('[useAutomationWebSocket] 🔸 Received testStep event:', step);
      if (step && typeof step === 'object') {
        setTestSteps(prev => {
          const newSteps = [...prev, {
            ...step,
            timestamp: step.timestamp || Date.now(),
            id: step.id || `step_${Date.now()}_${Math.random()}`
          }];
          return newSteps;
        });
        setCurrentStep(prev => prev + 1);
      }
    });
    cleanupFunctions.push(testStepCleanup);

    // Screenshot events
    const screenshotCleanup = on('screenshot', (data) => {
      if (!isActiveRunStreamEvent(data)) return;
      if (data) {
        setScreenshots(prev => {
          const newScreenshots = [...prev, {
            ...data,
            timestamp: data.timestamp || Date.now(),
            id: data.id || `screenshot_${Date.now()}_${Math.random()}`
          }];
          
          // Limit screenshots to prevent memory issues (keep last 50)
          return newScreenshots.slice(-50);
        });
      }
    });
    cleanupFunctions.push(screenshotCleanup);

    // Token usage: only check upper threshold (106). Refs set at connect from beforeConnect usageData; no API call here.
    const tokenUsageCleanup = on('tokenUsage', (usage) => {
      if (!isActiveRunStreamEvent(usage)) return;
      if (usage && typeof usage === 'object') {
        setTokenUsage(usage);
      }
      const onCreditLimitReached = onCreditLimitReachedRef.current;
      const onRunComplete = onScriptCreatorRunCompleteRef.current;
      if (!onCreditLimitReached) return;

      const tokensThisMessage = Math.floor(Number(usage?.input_tokens) || 0) + Math.floor(Number(usage?.output_tokens) || 0);
      cumulativeTokensThisRunRef.current += tokensThisMessage;

      const totalCredits = creditsUsedAtStartRef.current + cumulativeTokensThisRunRef.current / tokensPerCreditRef.current;
      if (totalCredits > scriptCreatorCapRef.current) {
        const msg = `Credits exhausted, Please upgrade your plan.`;
        setError(msg);
        setIsRunning(false);
        setTestCompleted(true);
        setTestCaseCompleteResult({ error: msg });
        onCreditLimitReached?.({ preserveData: true, message: msg });
        onRunComplete?.();
      }
    });
    cleanupFunctions.push(tokenUsageCleanup);

    // Hierarchical steps events
    const hierarchicalCleanup = on('hierarchicalSteps', (data) => {
      if (!isActiveRunStreamEvent(data)) return;
      console.log('[useAutomationWebSocket] 🔷 Received hierarchicalSteps event:', data);
      if (data) {
        setHierarchicalData(data);
      }
    });
    cleanupFunctions.push(hierarchicalCleanup);
    console.log('[useAutomationWebSocket] ✅ hierarchicalSteps listener registered');

    // Execution flow events
    const executionFlowCleanup = on('executionFlow', (data) => {
      if (!isActiveRunStreamEvent(data)) return;
      if (data) {
        const processedData = addSequentialStepIndices(data);
        setExecutionFlow(processedData);
      }
    });
    cleanupFunctions.push(executionFlowCleanup);

    const replayPlanCleanup = on('replayPlan', (data) => {
      if (!isActiveRunStreamEvent(data)) return;
      if (data) {
        setIsReplayPlanFetched(true);
        setReplayPlan(data);
      }
    });
    cleanupFunctions.push(replayPlanCleanup);

    // Test completion events — never accept when generation was aborted (active id cleared)
    const testCompleteCleanup = on('testComplete', (result) => {
      if (!shouldApplyGenerationEvent(activeGenerationIdRef.current, result)) {
        console.log('[useAutomationWebSocket] ⏭️ Ignoring stale testComplete for inactive/other run');
        return;
      }
      console.log('[useAutomationWebSocket] ✅ Received testComplete event:', result);
      setIsRunning(false);
      setTestCompleted(true);
      setTestCaseCompleteResult(result);
      activeGenerationIdRef.current = null;
      onScriptCreatorRunCompleteRef.current?.();
      if (result && result.error) {
        setError(result.error);
      }
    });
    cleanupFunctions.push(testCompleteCleanup);
    console.log('[useAutomationWebSocket] ✅ testComplete listener registered');

    // Error events
    const errorCleanup = on('error', (err) => {
      const errorMessage = err?.message || err || 'An error occurred';
      setError(errorMessage);
      setIsRunning(false);
    });
    cleanupFunctions.push(errorCleanup);

    // Replay events

    const replayStatusCleanup = on('replayStatus', (data) => {
      if (!data?.sessionId || data.sessionId !== replayExecutionIdRef.current) return;
    });
    cleanupFunctions.push(replayStatusCleanup);

    const replayStepStartCleanup = on('replayStepStart', (raw) => {
      if (!raw?.sessionId || raw.sessionId !== replayExecutionIdRef.current) return;
      const data = normalizeReplayStepStart(raw);
      const stepIndex = typeof data.stepIndex === 'number' ? data.stepIndex : Number(data.stepIndex);
      if (!Number.isFinite(stepIndex)) return;
      setReplayCurrentStepIndex(stepIndex);
      
      // Store step details including prepared arguments
      setReplayExecutionResults(prev => {
        if (!prev) {
          const initialSummary = { total: 0, passed: 0, failed: 0, successRate: 0 };
          return {
            results: [],
            summary: initialSummary,
            stepDetails: {
              [stepIndex]: {
                ...data,
                status: 'executing'
              }
            }
          };
        }
        // Do not merge previous stepDetails[stepIndex] — a new run would keep old result/reason until complete.
        return {
          ...prev,
          stepDetails: {
            ...prev.stepDetails,
            [stepIndex]: {
              ...data,
              status: 'executing'
            }
          }
        };
      });
      
      // Add to logs
      setExecutionLogs(prev => [...prev,
        {
          message: `\n📍 Starting step ${stepIndex}: ${data.tool}`,
          level: 'info',
          timestamp: Date.now()
        }
      ]);
    });
    cleanupFunctions.push(replayStepStartCleanup);

    const replayStepCompleteCleanup = on('replayStepComplete', (raw) => {
      if (!raw?.sessionId || raw.sessionId !== replayExecutionIdRef.current) return;
      const result = normalizeReplayStepComplete(raw);
      const stepIndex = typeof result.stepIndex === 'number' ? result.stepIndex : Number(result.stepIndex);
      if (!Number.isFinite(stepIndex)) return;

      const stepSucceeded =
        result.result?.success === true || result.result?.result?.success === true;

      setReplayExecutionResults(prev => {
        if (!prev) {
          const resultWithIndex = { ...result, stepIndex };
          return {
            results: [resultWithIndex],
            summary: {
              total: 1,
              passed: stepSucceeded ? 1 : 0,
              failed: stepSucceeded ? 0 : 1,
              successRate: stepSucceeded ? 100 : 0
            },
            stepDetails: {
              [stepIndex]: {
                result: resultWithIndex,
                screenshot: result.screenshot,
                status: result.status
              }
            }
          };
        }

        const resultWithIndex = { ...result, stepIndex };
        // Check if result for this stepIndex already exists, update it instead of adding duplicate
        const existingResultIndex = prev.results?.findIndex(r => r.stepIndex === stepIndex);
        const newResults = existingResultIndex >= 0
          ? prev.results.map((r, idx) => idx === existingResultIndex ? resultWithIndex : r)
          : [...(prev.results || []), resultWithIndex];
        const stepPass = (r) =>
          r.result?.success === true || r.result?.result?.success === true;
        const passed = newResults.filter(stepPass).length;
        const failed = newResults.filter((r) => !stepPass(r)).length;
        const total = newResults.length;
        
        return {
          ...prev,
          results: newResults,
          summary: {
            total,
            passed,
            failed,
            successRate: total > 0 ? Math.round((passed / total) * 100) : 0
          },
          stepDetails: {
            ...prev.stepDetails,
            [stepIndex]: {
              ...prev.stepDetails?.[stepIndex],
              result: resultWithIndex,
              screenshot: result.screenshot,
              status: result.status
            }
          }
        };
      });

      // Add completion status to logs
      setExecutionLogs(prev => [...prev,
        {
          message: `   ✓ Step ${stepIndex} ${result.status}`,
          level: result.status === 'failed' ? 'error' : 'success',
          timestamp: Date.now()
        }
      ]);

      // Stop-on-fail is enforced by the replay agent itself (breaks on failed step
      // and returns a normal result). Do NOT emit stopReplay here — that flips the
      // session's aborted flag on the server and the resulting replayError
      // ("Replay aborted by user") overwrites the real failure detail in the banner.
      // We still transition the UI eagerly so the user sees "completed" the moment
      // the failed step lands, and so the Stop button can't race-trigger an abort
      // during the brief tail while the agent is closing the browser server-side.
      if (result.status === 'failed') {
        setReplayExecuting(false);
        setReplayCurrentStepIndex(null);
      }
    });
    cleanupFunctions.push(replayStepCompleteCleanup);

    const replayCompleteCleanup = on('replayComplete', (raw) => {
      if (!raw?.sessionId || raw.sessionId !== replayExecutionIdRef.current) return;
      const data = normalizeReplayCompletePayload(raw);
      setReplayExecuting(false);
      setReplayCurrentStepIndex(null);
      onReplayCompleteRef.current?.();
      if (data.success) {
        setExecutionResults(data.results);
        // Add completion message to logs
        setExecutionLogs(prev => [...prev,
          {
            message: '\n✅ Test execution completed!',
            level: 'info'
          },
          {
            message: `Summary: ${data.results.summary.completed} completed, ${data.results.summary.failed} failed, ${data.results.summary.skipped} skipped`,
            level: 'info'
          }
        ]);
      }
    });
    cleanupFunctions.push(replayCompleteCleanup);

    const replayErrorCleanup = on('replayError', (data) => {
      if (!data?.sessionId || data.sessionId !== replayExecutionIdRef.current) return;
      setReplayExecuting(false);
      setReplayCurrentStepIndex(null);
      const msg = (data && (data.error ?? data.message)) || 'Execution failed';
      setReplayError(msg);
      setError(typeof msg === 'string' ? msg : 'Execution failed');
    });
    cleanupFunctions.push(replayErrorCleanup);

    const replayLogCleanup = on('replayLog', (data) => {
      if (!data?.sessionId || data.sessionId !== replayExecutionIdRef.current) return;
      const line = typeof data?.message === 'string' ? data.message : '';
      if (isLlmCompletionLogMessage(line) && onCreditLimitReachedRef.current && !usageCheckInProgressRef.current) {
        usageCheckInProgressRef.current = true;
        getTrialUsage().then((res) => {
          usageCheckInProgressRef.current = false;
          if (res?.status !== 'success' || !res?.data) return;
          if (!usageExceedsScriptCreatorCap(res.data)) return;
          finalizeCreditExhausted(res.data);
        }).catch(() => { usageCheckInProgressRef.current = false; });
      }
    });
    cleanupFunctions.push(replayLogCleanup);

    // Store cleanup functions
    cleanupFunctionsRef.current = cleanupFunctions;

    console.log(`[useAutomationWebSocket] ✅ All ${cleanupFunctions.length} event listeners registered successfully`);
    console.log('[useAutomationWebSocket] 🎧 Listening for events: log, testStep, screenshot, tokenUsage, hierarchicalSteps, executionFlow, replayPlan, testComplete, error, replayStatus, replayStepStart, replayStepComplete, replayComplete, replayError');

    // Cleanup on unmount
    return () => {
      console.log('[useAutomationWebSocket] 🧹 Cleaning up event listeners');
      cleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
      cleanupFunctionsRef.current = [];
    };
  }, [isConnected, on, scrollToBottom, disconnect, finalizeCreditExhausted]);

  // Emits replayTestPlanJson. Agent loads plan from DB via testcaseId — plan arg is unused (legacy callers).
  /** @returns {Promise<{ ok: true } | { ok: false, error: string }>} */
  const replayAutomation = useCallback(async (_plan, options = {}, testCaseId = null, executionId = null, environmentId = null, executionContext, projectId = null, llmId = null, model = null, isDynamic = false) => {
    const fail = (message) => {
      setReplayExecuting(false);
      setError(message);
      return { ok: false, error: message };
    };
    const safeTestCaseId =
      typeof testCaseId === 'string' && testCaseId.trim()
        ? testCaseId.trim()
        : typeof testCaseId === 'number' && Number.isFinite(testCaseId)
          ? String(testCaseId)
          : null;
    if (!safeTestCaseId) {
      return fail('Save the execution record to a test case before replay.');
    }
    if (!projectId) {
      return fail('Project context is required for replay.');
    }
    // Fresh socket so a prior stopped generate cannot deliver stale events into this replay.
    const connectResult = await ensureFreshConnection();
    if (!isWebSocketConnectSuccess(connectResult)) {
      return fail(
        getWebSocketConnectErrorMessage(connectResult, 'Failed to establish WebSocket connection')
      );
    }
    const usageRes = await getTrialUsage();
    const { ok, usage } = parseTrialUsageResponse(usageRes);
    if (!ok) {
      return fail('Unable to verify credits. Please try again.');
    }
    const replayGate = canStartAutomationReplay(usage);
    if (!replayGate.allowed) {
      return fail(replayGate.message);
    }
    setError(null);
    setReplayExecuting(true);
    setReplayExecutionResults(null);
    setReplayCurrentStepIndex(null);
    setReplayError(null);
    setExecutionResults(null);
    setReplayPlan(null);
    setIsReplayPlanFetched(false);
    activeGenerationIdRef.current = null;
    replayExecutionIdRef.current = executionId;
    replayExecutionContextRef.current = executionContext;
    replayOptionsRef.current = options;
    const replayPayload = {
      uniqueId: executionId,
      options: {
        stopOnError: options?.stopOnError !== false,
        directExecution: options?.directExecution !== false,
        useLLMFallback: options?.useLLMFallback !== false,
      },
      testcaseId: safeTestCaseId,
      environmentId: environmentId ?? null,
      projectId: projectId ?? null,
      llmId: typeof llmId === 'string' ? llmId : llmId != null ? String(llmId) : null,
      executionContext: executionContext
        ? {
            projectId: executionContext.projectId ?? null,
            organizationId: executionContext.organizationId ?? null,
            userId: executionContext.userId ?? null,
          }
        : null,
      sessionId: executionId,
      ...(typeof model === 'string' && model ? { model } : {}),
      ...(isDynamic && { isDynamic: true }),
    };
    const success = emit('replayTestPlanJson', replayPayload, (error) => {
      if (error) {
        setError(error.message || 'Failed to replay automation');
        setReplayExecuting(false);
      }
    });

    if (!success) {
      return fail('Failed to replay automation');
    }

    return { ok: true };
  }, [ensureFreshConnection, emit]);
  // Start automation
  /** @returns {Promise<{ ok: true } | { ok: false, error: string }>} */
  const startAutomation = useCallback(async (testCaseId, instructions = null, model, environmentId = null, executionContext, projectId = null, modelVersion = null, variablePayload = null) => {
    const fail = (message) => {
      activeGenerationIdRef.current = null;
      setError(message);
      setIsRunning(false);
      return { ok: false, error: message };
    };

    // Always open a fresh socket — reuse after Stop leaves prior-run events live (QPN-2087).
    console.log('[useAutomationWebSocket] 🔄 Ensuring fresh WebSocket connection...');
    const connectResult = await ensureFreshConnection();
    if (!isWebSocketConnectSuccess(connectResult)) {
      return fail(
        getWebSocketConnectErrorMessage(connectResult, 'Failed to establish WebSocket connection')
      );
    }
    setError(null);

    if (!instructions) {
      console.error('[useAutomationWebSocket] ❌ Instructions are required');
      return fail('Instructions are required');
    }

    const usageRes = await getTrialUsage();
    const { ok, usage } = parseTrialUsageResponse(usageRes);
    if (!ok) {
      return fail('Unable to verify credits. Please try again.');
    }
    const genGate = canStartAutomationGenerator(usage);
    if (!genGate.allowed) {
      return fail(genGate.message);
    }

    console.log('[useAutomationWebSocket] 🧹 Clearing previous data...');
    clearAutomationData();
    activeGenerationIdRef.current = testCaseId;
    setIsRunning(true);
    setIsReplayPlanFetched(false);
    setReplayPlan(null);
    setTestCompleted(false);
    setTestCaseCompleteResult(null);
    replayExecutionIdRef.current = null;

    const payload = {
      testId: testCaseId,
      instructions: instructions,
      model: model,
      modelVersion: modelVersion || undefined,
      environmentId: environmentId,
      executionContext,
      projectId: projectId,
      sessionId: testCaseId,
      ...(variablePayload ? { variablePayload } : {}),
    };

    console.log('[useAutomationWebSocket] 📤 EMITTING executeTest event');
    // Never print full payload in console (may include variable metadata and other sensitive context)
    console.log('[useAutomationWebSocket] 📤 Payload summary:', {
      testId: payload.testId,
      instructionCount: Array.isArray(payload.instructions) ? payload.instructions.length : 0,
      hasVariablePayload: !!payload.variablePayload,
      variableIds: payload.variablePayload ? Object.keys(payload.variablePayload.variableMetadata || {}).length : 0,
      environmentId: payload.environmentId,
      projectId: payload.projectId,
    });

    const success = emit('executeTest', payload, (error) => {
      if (error) {
        console.error('[useAutomationWebSocket] ❌ Emit callback error:', error);
        activeGenerationIdRef.current = null;
        setError(error.message || 'Failed to start automation');
        setIsRunning(false);
      } else {
        console.log('[useAutomationWebSocket] ✅ Emit callback - no error');
      }
    });

    console.log('[useAutomationWebSocket] 📤 emit() returned:', success);

    if (!success) {
      console.error('[useAutomationWebSocket] ❌ Failed to send automation request');
      return fail('Failed to send automation request');
    }
    console.log('[useAutomationWebSocket] ✅ Automation request sent successfully');

    return { ok: true };
  }, [emit, clearAutomationData, ensureFreshConnection]);

  // Stop automation - invalidate run id, emit stop, then hard-disconnect (QPN-2087)
  const stopAutomation = useCallback((executionId, executionContext) => {
    activeGenerationIdRef.current = null;
    setIsRunning(false);
    // Prevent a prior/late passed overallStatus from surviving into the abort UI.
    setTestCaseCompleteResult({
      overallStatus: { status: 'partial', message: USER_ABORTED_EXECUTION_MESSAGE },
      abortedByUser: true,
    });

    let success = false;
    if (isConnected) {
      success = emit('stopTest', {
        testId: executionId,
        executionContext
      }, (error) => {
        if (error) {
          setError(error.message || 'Failed to stop automation');
        }
      });
    }

    disconnect();
    return success;
  }, [isConnected, emit, disconnect]);

  // Stop replay automation - clear replay id, emit stop, hard-disconnect
  const stopReplayAutomation = useCallback((executionId, executionContext) => {
    replayExecutionIdRef.current = null;
    setReplayExecuting(false);
    setReplayCurrentStepIndex(null);
    if (isConnected) {
      emit('stopReplay', { uniqueId: executionId, executionContext }, (error) => {
        if (error) {
          setError(error.message || 'Failed to stop replay automation');
        }
      });
    }
    disconnect();
  }, [emit, isConnected, disconnect]);

  // Pause automation
  const pauseAutomation = useCallback(() => {
    if (!isConnected) {
      setError('WebSocket not connected');
      return false;
    }

    return emit('pauseAutomation', {
      timestamp: Date.now()
    }, (error) => {
      if (error) {
        setError(error.message || 'Failed to pause automation');
      }
    });
  }, [isConnected, emit]);

  // Resume automation
  const resumeAutomation = useCallback(() => {
    if (!isConnected) {
      setError('WebSocket not connected');
      return false;
    }

    return emit('resumeAutomation', {
      timestamp: Date.now()
    }, (error) => {
      if (error) {
        setError(error.message || 'Failed to resume automation');
      }
    });
  }, [isConnected, emit]);

  // Clear error
  const clearAutomationError = useCallback(() => {
    setError(null);
    clearError();
  }, [clearError]);

  // Create execution record
  const createExecutionRecord = useCallback(async (testCaseId, options = {}) => {
    if (!testCaseId) {
      setError('Test case ID is required to create execution record');
      return { success: false, error: 'Test case ID is required' };
    }

    // Check if test is still running
    if (isRunning) {
      setError('Cannot create execution record while test is still running');
      return { success: false, error: 'Test is still running' };
    }

    if (!executionFlow) {
      setError('No execution flow data available');
      return { success: false, error: 'No execution flow data available' };
    }

    // Check if test completed successfully (optional validation)
    if (!testCompleted) {
      setError('Cannot create execution record - test has not completed');
      return { success: false, error: 'Test has not completed' };
    }

    try {
      // Import the API function dynamically to avoid circular dependencies
      const { updateExecutionRecord } = await import('../../../utils/api');
      
      const result = await updateExecutionRecord({
        testCaseId,
        plan: executionFlow,
        ...options
      });

      if (result.status === 1 || result.success) {
        return { success: true, data: result };
      } else {
        const errorMessage = result.error || 'Failed to create execution record';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error.message || 'Error creating execution record';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [executionFlow, isRunning, testCompleted]);

  // Get automation statistics
  const getAutomationStats = useCallback(() => {
    return {
      totalLogs: logs.length,
      totalSteps: testSteps.length,
      currentStep,
      totalScreenshots: screenshots.length,
      tokenUsage,
      isRunning,
      testCompleted,
      hasError: !!(error || connectionError)
    };
  }, [logs.length, testSteps.length, currentStep, screenshots.length, tokenUsage, isRunning, testCompleted, error, connectionError]);

  return {
    // Connection state
    isConnected,
    isConnecting,
    connectionError,

    // Automation state
    logs,
    testSteps,
    currentStep,
    screenshots,
    tokenUsage,
    hierarchicalData,
    executionFlow,
    isRunning,
    testCompleted,
    error,
    replayPlan,
    isReplayPlanFetched,
    testCaseCompleteResult,

    // Replay state
    replayExecuting,
    replayCurrentStepIndex,
    replayExecutionResults,
    replayError,
    executionLogs,
    executionResults,
    stopReplayAutomation,

    // Connection methods
    connect,
    disconnect,

    // Communication methods
    emit,
    on,

    // Automation methods
    startAutomation,
    stopAutomation,
    pauseAutomation,
    resumeAutomation,
    clearAutomationData,
    clearAutomationError,
    createExecutionRecord,
    markReplayStarting,
    replayAutomation,

    // Utility methods
    getAutomationStats,
    scrollToBottom,
    logsEndRef
  };
};

export default useAutomationWebSocket;
